// src/shared/api/interceptors/circuitBreaker.ts
import { type InternalAxiosRequestConfig } from 'axios';
import { axiosInstance } from '../client';

/**
 * A last-resort limiter on how often one endpoint may fail before the client
 * stops asking.
 *
 * This is NOT the fix for any particular loop — those get fixed at their
 * source. It exists because a request loop is uniquely destructive and
 * uniquely easy to reintroduce: a deleted user whose `/auth/profile` answered
 * 403 forever produced roughly 600 requests a second behind a blank page, and
 * every layer that should have stopped it (React Query's `retry: false`, the
 * 401-only retry interceptor, RequirePermission's error branch) was already
 * correct. The loop lived in a render cycle ABOVE all of them, where no
 * per-query setting can reach.
 *
 * So the guarantee is placed at the one point every call passes through:
 * whatever the cause, one endpoint cannot be hammered indefinitely.
 *
 * Deliberately status-agnostic. The signal is VOLUME, not which code came
 * back — a 404 loop, a 500 loop and a 403 loop are equally broken, and any
 * status filter is a hole waiting for the next variant to fall through.
 */

/** Failures of one endpoint within the window before the breaker opens. */
const FAILURE_THRESHOLD = 8;
/** Sliding window over which failures are counted. */
const WINDOW_MS = 10_000;
/** How long an open breaker rejects before allowing a probe through. */
const COOLDOWN_MS = 30_000;

/** Marks the synthetic rejection so callers can tell it from a real response. */
export const CIRCUIT_OPEN_CODE = 'CLIENT_CIRCUIT_OPEN';

interface BreakerEntry {
  /** Timestamps of recent failures, oldest first, pruned to the window. */
  failures: number[];
  /** When the breaker opened, or null while closed. */
  openedAt: number | null;
}

const breakers = new Map<string, BreakerEntry>();

/**
 * Endpoint identity: method + namespace-stripped path, query dropped.
 *
 * Dropping the query is what makes the counter meaningful — a loop that
 * appends a cache-buster or a changing timestamp would otherwise mint a new
 * key per request and never trip anything. Keeping the PATH (ids included)
 * means one broken record cannot silence a healthy sibling.
 *
 * Stripping `/api/v2/{merchant,system}/` is not cosmetic. Axios runs REQUEST
 * interceptors last-registered-first, so this one sees the short path the
 * caller wrote (`/auth/profile`) while the namespace interceptor has not run
 * yet; by the time the error comes back, `config.url` is the rewritten
 * absolute URL (`https://…/api/v2/merchant/auth/profile`). Keyed literally,
 * the outbound check and the failure counter would use different keys and the
 * breaker would never trip — it would look installed and do nothing.
 */
function endpointKey(config: {
  method?: string;
  url?: string;
  baseURL?: string;
}): string {
  const method = (config.method ?? 'get').toUpperCase();
  const raw = config.url ?? '';
  // Works for absolute and relative URLs alike; the base is a throwaway used
  // only so relative paths parse.
  let path: string;
  try {
    path = new URL(raw, config.baseURL || 'http://x').pathname;
  } catch {
    path = raw.split('?')[0];
  }
  path = path
    .replace(/^\/+/, '')
    .replace(/^api\/v2\/(merchant|system)\//, '')
    .replace(/^api\/v2\//, '')
    .replace(/^api\/v1\//, '');
  return `${method} /${path}`;
}

function circuitOpenError(key: string) {
  return Object.assign(
    new Error(
      `Too many failed requests to ${key}. Stopped to avoid a request loop.`,
    ),
    {
      code: CIRCUIT_OPEN_CODE,
      // Shaped like an axios error so every existing handler — the global
      // error reporter, useMutationErrorHandler, per-page catches — reads it
      // without a special case. 503 is the honest status: the client has
      // decided this endpoint is unavailable for now.
      response: {
        status: 503,
        data: { code: CIRCUIT_OPEN_CODE, message: 'Request loop stopped' },
      },
      isCircuitOpen: true as const,
    },
  );
}

/** True when the breaker for `key` is open and still inside its cooldown. */
function isOpen(entry: BreakerEntry, now: number): boolean {
  if (entry.openedAt === null) return false;
  if (now - entry.openedAt < COOLDOWN_MS) return true;
  // Cooldown elapsed — close it and let exactly one probe through. If that
  // probe fails the window is empty, so it takes another FAILURE_THRESHOLD to
  // re-open; that is intentional. A permanently broken endpoint then costs 8
  // requests per 30s instead of thousands per second, and a recovered one
  // heals with no user action.
  entry.openedAt = null;
  entry.failures = [];
  return false;
}

export function recordApiFailure(key: string, now = Date.now()): void {
  const entry = breakers.get(key) ?? { failures: [], openedAt: null };
  entry.failures = entry.failures.filter((t) => now - t < WINDOW_MS);
  entry.failures.push(now);
  if (entry.failures.length >= FAILURE_THRESHOLD) {
    entry.openedAt = now;
  }
  breakers.set(key, entry);
}

export function recordApiSuccess(key: string): void {
  // A success proves the endpoint works; forget the history entirely rather
  // than letting stale failures accumulate toward a spurious trip.
  breakers.delete(key);
}

/** Clear all breakers — used on logout, and by tests. */
export function resetCircuitBreakers(): void {
  breakers.clear();
}

/** Introspection for tests and diagnostics. */
export function isCircuitOpenFor(key: string, now = Date.now()): boolean {
  const entry = breakers.get(key);
  return entry ? isOpen(entry, now) : false;
}

export function registerCircuitBreakerInterceptor(): void {
  axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const key = endpointKey(config);
    const entry = breakers.get(key);
    if (entry && isOpen(entry, Date.now())) {
      // Reject before the request leaves the browser. This is the whole point
      // — an open breaker must cost no network at all.
      return Promise.reject(circuitOpenError(key));
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      recordApiSuccess(endpointKey(response.config));
      return response;
    },
    (error) => {
      // Our own synthetic rejection: already counted, and counting it again
      // would keep the breaker open forever.
      if ((error as { isCircuitOpen?: boolean })?.isCircuitOpen) {
        return Promise.reject(error);
      }
      const config = (error as { config?: { method?: string; url?: string; baseURL?: string } })
        ?.config;
      if (config) recordApiFailure(endpointKey(config));
      return Promise.reject(error);
    },
  );
}
